import React, { useContext, useEffect, useState } from 'react';
import { Checkbox } from '@wds/checkbox';
import { Typography } from '@wds/typography';
import { ErrorCard } from '@sm/webassets';
import { Box } from '@wds/box';
import { SearchConsumer } from '../SearchContext';

import CategoriesContext from '../CategoriesContext';
import FilterNavSkeleton from './FilterNavSkeleton';

export const buildCategoryTree = categories => {
  const result = [];

  // Create the "Others" category
  const noParentCategories = {
    id: 0,
    title: 'Others',
    description: '',
    key: 'others',
    parentId: null,
    children: [],
  };

  // Get the items with no parents
  const parents = categories.filter(item => item.parentId === null);

  // For each parent, add it to the result and find its children
  parents.forEach(parent => {
    const parentWithChildren = { ...parent, children: [] };

    categories.forEach(item => {
      if (item.parentId === parent.id) {
        parentWithChildren.children.push(item);
      }
    });

    // If a parent has no children, add it to the "Others" category
    if (parentWithChildren.children.length === 0) {
      noParentCategories.children.push(parent);
    } else {
      // Otherwise, add it to the result
      result.push(parentWithChildren);
    }
  });

  // Add the "Others" category to the result
  if (noParentCategories.children.length > 0) {
    result.push(noParentCategories);
  }
  // Sort the result array by title
  result.sort(({ key: aKey = '' }, { key: bKey = '' }) => aKey?.localeCompare(bKey));

  // Sort the children arrays by title
  result.forEach(item => {
    item.children.sort(({ key: aKey = '' }, { key: bKey = '' }) => aKey?.localeCompare(bKey));
  });

  return result;
};

const FilterNav = () => {
  const { isLoading: isCategoriesLoading, categories = [] } = useContext(CategoriesContext);
  const [categoryTree, setCategoryTree] = useState([]);

  useEffect(() => {
    if (categories.length) {
      setCategoryTree(buildCategoryTree(categories));
    }
  }, [categories]);

  return (
    <SearchConsumer>
      {({ filters, updateFilter }) => {
        const isChecked = filter => filters.includes(filter);
        const handleChange = filter => {
          updateFilter(isChecked(filter) ? filters.filter(f => f !== filter) : [...filters, filter]);
        };

        const renderFilter = ({ name, title, key, id }) => (
          <div key={id || key}>
            <Checkbox
              label={name || title}
              id={id || key}
              data-testid="FilterNav__CategoryItem"
              onChange={() => handleChange(id)}
              checked={isChecked(id)}
            />
          </div>
        );

        const renderCategoryTree = () => {
          return categoryTree.map(parent => (
            <div key={parent.id}>
              <Box mt={3} mb={2}>
                <Typography variant="cardTitle" component="h3">
                  {parent.title}
                </Typography>
              </Box>
              {parent.children.length && parent.children.map(cat => renderFilter(cat))}
            </div>
          ));
        };

        return (
          <div>
            <Typography variant="pageTitle">Filter</Typography>
            <Choose>
              <When condition={isCategoriesLoading}>
                <FilterNavSkeleton />
              </When>
              <When condition={!isCategoriesLoading && categories.length === 0}>
                <ErrorCard />
              </When>
              <Otherwise>{renderCategoryTree()}</Otherwise>
            </Choose>
          </div>
        );
      }}
    </SearchConsumer>
  );
};

export default FilterNav;
